import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"  
import { motion } from "framer-motion"
import { User, Lock } from "lucide-react"
import { useAuth, useClientConfig } from '@workspace/public-utils'
import { Seo } from "../../components/Seo"

const LoginPage = () => {
  const config = useClientConfig()
  const [formData, setFormData] = useState({
    name: "",
    last4: ""
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError("")

    try {
      await login(formData.name, formData.last4)
      navigate("/dashboard")
    } catch (err: any) {
      console.error('Login error:', err)
      
      // Extract detailed error message
      let errorMessage = "로그인에 실패했습니다."
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail
        if (detail.includes('존재하지 않는')) {
          errorMessage = detail
        } else if (detail.includes('틀린')) {
          errorMessage = detail
        } else {
          errorMessage = detail
        }
      } else if (err.response?.status === 401) {
        errorMessage = "사용자명 또는 비밀번호가 올바르지 않습니다."
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // OAuth removed

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-happiness-primary/5 via-white to-happiness-secondary/5 py-12 px-4 sm:px-6 lg:px-8">
      <Seo title="로그인" canonicalPath="/auth/login" noindex />
      <div className="max-w-md mx-auto">
        {/* Navigation */}
        <div className="mb-6 flex justify-between items-center">
          <button 
            onClick={handleGoBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-happiness-primary transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:ring-offset-2 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            뒤로가기
          </button>
          <Link 
            to="/" 
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-happiness-primary to-happiness-secondary rounded-lg hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:ring-offset-2 transform hover:scale-[1.02]"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            홈으로
          </Link>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-block">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-happiness-primary to-happiness-secondary rounded-full flex items-center justify-center mb-6 shadow-lg hover:shadow-xl transition-shadow">
              <span className="text-3xl text-white font-bold">{config.logoIconText}</span>
            </div>
            <h1 className="gradient-text-happiness text-4xl font-bold mb-2 hover:opacity-80 transition-opacity">
              {config.brandName}
            </h1>
          </Link>
          <p className="text-gray-600">
            당신의 행운이 시작되는 곳
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
        >
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">로그인</h2>
            <p className="text-gray-600">계정에 로그인하여 복권을 구매하세요</p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                이름
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:border-transparent transition-colors"
                  placeholder="이름을 입력하세요"
                />
              </div>
            </div>

            <div>
              <label htmlFor="last4" className="block text-sm font-medium text-gray-700 mb-2">
                휴대폰 번호 마지막 4자리
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="password"
                  id="last4"
                  name="last4"
                  value={formData.last4}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-happiness-primary focus:border-transparent transition-colors"
                  placeholder="마지막 4자리를 입력하세요"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-happiness-primary focus:ring-happiness-primary focus:ring-offset-0"
                />
                <span className="ml-2 text-sm text-gray-600">로그인 상태 유지</span>
              </label>
              <Link
                to="/auth/forgot-password"
                className="text-sm text-happiness-primary hover:text-happiness-primary/80 transition-colors"
              >
                비밀번호 찾기
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-happiness-primary to-happiness-primary/80 hover:from-happiness-primary/90 hover:to-happiness-primary/70 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  로그인 중...
                </div>
              ) : (
                "로그인"
              )}
            </button>
          </form>

          {/* OAuth login removed */}

          <div className="text-center mt-6 pt-6 border-t border-gray-200">
            <p className="text-gray-600">
              계정이 없으신가요?{" "}
              <Link
                to="/auth/register"
                className="text-happiness-primary hover:text-happiness-primary/80 font-semibold transition-colors"
              >
                회원가입
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default LoginPage
